import { Check, X } from 'lucide-react';
import type { ShotOutcome } from '@golf/common';

/* Deux réponses, quand l'atelier a son propre critère.
 *
 * ── Pourquoi le pavé ordinaire ne convient pas ──────────────────────────────
 *
 * Il propose « bon contact » et six fautes de frappe — slice, hook, gratte,
 * top, socket. C'est le vocabulaire d'un seau de balles au practice, et il ne
 * dit rien d'un putt rentré à un mètre ni d'une balle arrêtée à moins de trois
 * mètres du trou. Sur un atelier d'examen, la question n'est pas COMMENT la
 * balle est partie mais si elle compte. Demander une faute de frappe sur un
 * putt raté, c'est demander au joueur de traduire, et il traduira mal.
 *
 * ── Ce qui est enregistré derrière ──────────────────────────────────────────
 *
 * Les mêmes deux issues que partout : `center` pour un point, `miss` pour rien.
 * Aucune table nouvelle, aucune statistique qui diverge — le taux de réussite
 * d'un atelier se calcule comme celui d'un club, et le point n'est qu'une
 * lecture du centrage. Ce qui change est la QUESTION posée, pas la réponse
 * stockée.
 *
 * ── Le critère est écrit sur le bouton ──────────────────────────────────────
 *
 * « Réussi » tout seul obligerait à se souvenir de ce qui compte, cinq balles
 * après avoir lu le règlement. Le bouton le redit à chaque balle : c'est la
 * seule chose qu'on lit entre deux coups.
 */
export function PavePoints({ critere, onPick, disabled }: {
    /** Ce qui vaut un point, tel que l'atelier le formule. */
    critere: string;
    onPick: (outcome: ShotOutcome) => void;
    disabled?: boolean;
}) {
    return (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
            <button type="button" disabled={disabled}
                onClick={() => onPick('center')}
                className="btn btn-primary min-h-0 w-full flex-1 flex-col gap-1 py-4 shadow-md
                           disabled:opacity-40">
                <Check size={30} strokeWidth={3} aria-hidden="true" className="text-accent" />
                <span className="font-score text-2xl leading-none">+1 point</span>
                {/* Le critère, en petit sous le point : on ne le lit qu'une
                    fois, mais il doit être là quand un doute vient. */}
                <span className="max-w-full truncate px-2 text-xs font-normal opacity-85">
                    {critere}
                </span>
            </button>

            {/* Manqué prend MOINS de place que réussi, et c'est délibéré : sur
                un atelier d'examen on vise la réussite, et c'est elle qu'on
                doit pouvoir toucher sans regarder. La brique dit le reste. */}
            <button type="button" disabled={disabled}
                onClick={() => onPick('miss')}
                className="flex h-16 w-full shrink-0 items-center justify-center gap-3
                           rounded-field border-2 text-lg font-bold transition-transform
                           active:scale-[0.99] disabled:opacity-40"
                style={{
                    borderColor: 'var(--brique-bord)',
                    backgroundColor: 'var(--brique)',
                    color: 'var(--brique-encre)',
                }}>
                <X size={24} strokeWidth={3} aria-hidden="true" />
                Manqué
            </button>
        </div>
    );
}
